import React, { useState, useEffect } from 'react';
import { Gradient } from '../models/gradient';
import { ItemBasicInformation } from '../models/item-basic-information';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import { EditType } from '../models/enums';
import { MessageBoxReply } from '../models/message-box-reply';
import { CrudFunctions } from '../utilities/crud-functions';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { MessageBoxReplyType, ModalMessageType } from '../models/enums';

import DataGrid, {
    Column,
    Button as ColumnButton,
    Selection,
    Editing,
    Paging,
    LoadPanel,
    Toolbar,
    Item as ToolbarItem,
} from 'devextreme-react/data-grid';
import { Popup as DialogPopup } from 'devextreme-react/popup';

const PENDING_CHANGES = 100;

let key = 1;

export interface Props {
    benchSheetId: string;
    isIsocratic?: boolean;
    isReadOnly?: boolean;
    title?: string;
    onClose?: () => void;
}

export default function GradientsData(props: Props) {
    const [gradients, setGradients] =
        useState<Gradient[]>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getGradientsData();
    }, []);

    key++;

    const canAddRow = () => {
        if (props.isIsocratic && gradients && gradients.length > 1)
            return false;

        return true;
    }

    return (
        <div>
            <DataGrid
                width="100%"
                height="12rem"
                wordWrapEnabled={true}
                keyExpr="gradientId"
                dataSource={gradients ?? []}
                onInitNewRow={onInitNewGradientRow}
                onRowInserting={onGradientRowInserting}
                onRowInserted={onGradientRowInserted}
                onRowRemoving={onGradientRowRemoving}
                onRowRemoved={onGradientRowRemoved}
                onRowUpdating={onGradientRowUpdating}
                showBorders={true}
                rowAlternationEnabled={true}
                showRowLines={true} >

                <LoadPanel enabled={true} />
                <Selection mode={'single'} />
                <Paging enabled={false} />

                <Column name="bc0" type="buttons" width='6rem'>
                    <ColumnButton name="edit" hint="edit row" />
                    <ColumnButton name="delete" hint="delete row" />
                    <ColumnButton hint="certificate" icon="info"
                        onClick={onShowInfoButtonClick} />
                </Column>

                <Column dataField={'rowNumber'} caption="" dataType="number" width={'3em'} allowEditing={false} />
                <Column dataField={'time'} caption="Time (min)" dataType="number" width={'20%'} />
                <Column dataField={'mpa'} caption="MPA (%)" format="#" width={'20%'}
                    cellRender={gradientValueCellRender} />
                <Column dataField={'mpb'} caption="MPB (%)" dataType="number" width={'20%'}
                    cellRender={gradientValueCellRender} />QA
                <Column dataField={'mpc'} caption="MPC (%)" dataType="number" width={'20%'}
                    cellRender={gradientValueCellRender} />
                <Column dataField={'mpd'} caption="MPD (%)" dataType="number" width={'20%'}
                    cellRender={gradientValueCellRender} />

                <Toolbar>
                    <ToolbarItem location='before'>
                        <div className="pw-datagrid-toolbar-template">
                            <div className="pw-datagrid-toobar-title">{props.title ? props.title : 'Gradients'}</div>
                        </div>
                    </ToolbarItem>
                    <ToolbarItem location='after' name='addRowButton' />
                </Toolbar>

                <Editing mode="row"
                    useIcons={true}
                    allowAdding={canAddRow ? true : false}
                    allowUpdating={true}
                    allowDeleting={true} />
            </DataGrid>

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata}
                    onReply={onGlobalDialogReply} onClose={onGlobalDialogClose} />
            }
        </div>
    );

    function checkGradientPercentageSum(gradient: Gradient) {
        if (!gradient)
            return;

        /* LEAVE FOR NOW - I want a way to show at component level that the rows do not add up to 100%
        if ((gradient.mpa ? gradient.mpa : 0) + (gradient.mpb ? gradient.mpb : 0) +
            (gradient.mpc ? gradient.mpc : 0) + (gradient.mpd ? gradient.mpd : 0) > 100) {

            //use Toast instead:
            XXX.publish(ModalSubscriberTopic, { modalType: InformationModal, message: "This gradient percentages total more than 100.", title: "Gradient" });
        }
        */
    }

    // Insert step 1 - pre-fill new row with next row number
    function onInitNewGradientRow(e: any) {
        const maxRowNumber = gradients.reduce((max, item) =>
            Math.max(max, item.rowNumber || 0), 0);

        const nextRowNumber = maxRowNumber + 1;

        e.data.rowNumber = nextRowNumber;
        e.data.benchSheetId = props.benchSheetId
    }

    // Insert step 2 - make POST request
    function onGradientRowInserting(e: any) {
        e.cancel = insertGradient(e.data).then((insertedRecord) => {
            // overwrite datagrid row with record from POST response
            // this will also implicitly set cancel to false (success) and trigger onRowInserted
            e.data = insertedRecord;
        });
    }

    // Insert step 3 - sync state after insertion succeeds
    async function onGradientRowInserted(e: any) {
        await getGradientsData();
    }

    function onGradientRowUpdating(e: any) {
        const updatedRow = { ...e.oldData, ...e.newData };
        // .then(() => false) ensures the promise resolves to false (success) for DevExtreme
        e.cancel = updateGradient(updatedRow).then(() => false);
    }

    function onGradientRowRemoving(e: any) {
        // Map cancel to false on success so DevExtreme removes the row from UI
        e.cancel = deleteGradient(e.key).then(() => false);
    }

    // After successful deletion, resync state with fresh data
    // Refresh needed since the backend deletion method may update other rows
    async function onGradientRowRemoved(e: any) {
        await getGradientsData();
    }


    // CRUD functions

    async function getGradientsData() {
        await CrudFunctions.getData(`benchsheets/hplcgradientsbybenchsheetid/${props.benchSheetId}`)
            .then(data => {
                setGradients(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Gradients");
            });
    }

    async function insertGradient(gradient: Gradient) {
        try {
            const insertedGradient = await CrudFunctions.postData("benchsheets/hplcgradient", gradient);
            return insertedGradient;
        } catch (err) {
            showMessage(err, ModalMessageType.Error, "Error inserting gradient.");
            throw err;
        }
    }

    async function updateGradient(gradient: Gradient) {
        try {
            const updatedGradient = await CrudFunctions.putData("benchsheets/hplcgradient", gradient);
            return updatedGradient;
        } catch (err) {
            showMessage(err, ModalMessageType.Error, "Error updating gradient");
            throw err;
        }
    }

    async function deleteGradient(gradientId: string) {
        try {
            await CrudFunctions.deleteData(`benchsheets/hplcgradient/${props.benchSheetId}/${gradientId}`);
        } catch (err) {
            showMessage(err, ModalMessageType.Error, "Error deleting row");
            throw err;
        }
    }


    //render

    function gradientValueCellRender(e: any) {
        if (e.data) {
            if (e.data.mpa && e.data.mpb && e.data.mpc && e.data.mpd &&
                (e.data.mpa + e.data.mpb + e.data.mpc + e.data.mpd !== 100)) {
                return <div className="invalid-data">{e.value}</div>;
            }
            else {
                return <div>{e.value}</div>;
            }
        }
        else
            return <div></div>;
    }

    //show/hide

    function onShowInfoButtonClick(e: any) {
        if (gradients) {
            let entry = gradients.find(a => a.gradientId === e.row.key)

            if (entry) {
                console.log("info click:", entry);
                let itemBasicInformation: ItemBasicInformation = {
                    rowPointer: entry.gradientId,
                    createTimestamp: entry.createTimestamp,
                    lastModifiedTimestamp: entry.modifyTimestamp,
                    createUser: entry.createUser,
                    modifyUser: entry.modifyUser
                };

                setItemBasicInformation(itemBasicInformation);
                setItemBasicInformationDialogVisible(true);

            }
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }

    function onGlobalDialogReply(reply: MessageBoxReply) {
        console.log("onGlobalDialogReply");
        console.log(reply);

        if (reply.code === PENDING_CHANGES) {
            if (reply.reply !== MessageBoxReplyType.Yes) {
                setGlobalDialogVisible(false);
            }
            else {
                closeDialog();
            }
        }
    }

    async function closeDialog() {
        if (props.onClose)
            props.onClose();
    }

    function onGlobalDialogClose() {
        clearGlobalDialog();
    }
}

